/**
 * Storage utilities scoped by user to prevent cross-account data leakage.
 */

/**
 * Safe wrapper for localStorage/sessionStorage to prevent crashes in restricted browsers (e.g. private mode).
 */
export const safeStorage = {
  get: (key: string, storage: Storage = localStorage): string | null => {
    try {
      return storage.getItem(key);
    } catch (e) {
      console.warn(`[storage] Failed to get ${key} from ${storage === localStorage ? "localStorage" : "sessionStorage"}:`, e);
      return null;
    }
  },
  set: (key: string, value: string, storage: Storage = localStorage): boolean => {
    try {
      storage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn(`[storage] Failed to set ${key} in ${storage === localStorage ? "localStorage" : "sessionStorage"}:`, e);
      return false;
    }
  },
  remove: (key: string, storage: Storage = localStorage): boolean => {
    try {
      storage.removeItem(key);
      return true;
    } catch (e) {
      console.warn(`[storage] Failed to remove ${key} from ${storage === localStorage ? "localStorage" : "sessionStorage"}:`, e);
      return false;
    }
  },
  clear: (storage: Storage = localStorage): boolean => {
    try {
      storage.clear();
      return true;
    } catch (e) {
      console.warn(`[storage] Failed to clear ${storage === localStorage ? "localStorage" : "sessionStorage"}:`, e);
      return false;
    }
  }
};

const APP_PREFIXES = [
  "tickethall_",
  "archived_tickets",
  "ticket-hall-",
  "checkout_order_id",
  "promoter_",
  "auth_redirect_to",
  "event_viewed_",
  "sound_enabled",
  "onboarding_done",
  "quick_replies_",
  "urgency_overrides_",
  "staff_instructions_",
  "fc_categories_",
  "ps_",
  "become_",
];

/** Remove all application-specific keys from localStorage. */
export function clearAppLocalStorage() {
  const toRemove: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (APP_PREFIXES.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => safeStorage.remove(k, localStorage));
  } catch (e) {
    console.error("[storage] Failed to clear app localStorage:", e);
  }
}

/** Remove all application-specific keys from sessionStorage. */
export function clearAppSessionStorage() {
  const toRemove: string[] = [];
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key) continue;
      if (APP_PREFIXES.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => safeStorage.remove(k, sessionStorage));
  } catch (e) {
    console.error("[storage] Failed to clear app sessionStorage:", e);
  }
}

/** Keys scoped to a user (or anonymous). */
export const storageKeys = {
  cart: (userId: string | null) => (userId ? `tickethall_cart:${userId}` : "tickethall_cart"),
  cartExpiry: (userId: string | null) => (userId ? `tickethall_cart_expiry:${userId}` : "tickethall_cart_expiry"),
  archivedTickets: (userId: string | null) => (userId ? `archived_tickets:${userId}` : "archived_tickets"),
  checkoutOrderId: (userId: string | null) => (userId ? `checkout_order_id:${userId}` : "checkout_order_id"),
};
